import { PageContainer, ProTable, type ProColumns } from '@ant-design/pro-components'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Alert, App, Button, Space, Tag, Typography } from 'antd'
import { Plus } from 'lucide-react'
import type { AccountDisplay } from '@super-mega/server/contracts/profile'
import { TableRowActions } from '@/components/table-row-actions.tsx'
import { Authorized } from '@/features/authorization/authorized.tsx'
import {
  DEFAULT_ORGANIZATION_SEARCH,
  OPERATION_MODE,
  organizationSearchSchema,
  type OrganizationListSearch
} from '@/features/master-data/index.ts'
import { UserAvatarName } from '@/features/profile/index.ts'
import { usePageTableNavigation } from '@/hooks/index.ts'
import { usePersistedListQuery } from '@/hooks/use-persisted-list-query.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { createListQueryPersistence } from '@/utils/list-query-persistence.ts'

type OperationMode = 'agent' | 'selfOperated'

type OrganizationQuery = OrganizationListSearch

type OrganizationSearchFormValues = Pick<OrganizationQuery, 'keyword' | 'operationMode' | 'status'>

type OrganizationRow = {
  siteCount: number
  id: number
  name: string
  unifiedSocialCreditCode: string | null
  legalRepresentative: string | null
  registeredAddress: string
  operationMode: OperationMode
  isDisable: boolean
  creator: AccountDisplay
  createdAt: Date
  updatedAt: Date
}

const listQuery = createListQueryPersistence(
  'super-mega:master-data:legal-entities:list-query',
  organizationSearchSchema
)

export const Route = createFileRoute('/_authenticated/master-data/legal-entities/')({
  beforeLoad: listQuery.restore,
  component: LegalEntitiesPage,
  head: () => ({ meta: [{ title: '主体设置 · Super Mega' }] }),
  validateSearch: listQuery.validateSearch
})

function LegalEntitiesPage() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { message, modal } = App.useApp()
  const { currentSession } = Route.useRouteContext()
  const navigate = Route.useNavigate()
  const { search: query, navigateSearch } = usePersistedListQuery({
    persistence: listQuery,
    raw: Route.useSearch(),
    accountId: currentSession.id,
    navigate
  })
  const canManage =
    currentSession.isSuperAdmin ||
    currentSession.permissions.some(
      (permission) => permission.startsWith('organization.') && !permission.endsWith('.read')
    )
  const organizations = useQuery({
    ...trpc.organization.list.queryOptions(query),
    placeholderData: keepPreviousData
  })
  const setDisabled = useMutation(trpc.organization.setDisabled.mutationOptions())
  const deleteOrganization = useMutation(trpc.organization.delete.mutationOptions())

  const confirmDelete = (organization: OrganizationRow) => {
    modal.confirm({
      title: `永久删除主体「${organization.name}」（ID：${organization.id}）？`,
      content: '删除后无法恢复。仅允许删除没有关联场地、订单、账单或资金流水的主体，停用和历史记录也会阻止删除。',
      focusable: { autoFocusButton: 'cancel' },
      okText: '永久删除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteOrganization.mutateAsync(organization.id)
        } catch (error) {
          void message.error(getErrorMessage(error, '删除主体失败'))
          throw error
        }
        void message.success('经营主体已删除')
        await Promise.all([
          queryClient.invalidateQueries(trpc.organization.list.queryFilter()),
          queryClient.invalidateQueries(trpc.organization.detail.queryFilter(organization.id))
        ])
        if (organizations.data?.data.length === 1 && query.current > 1) {
          await navigateSearch({
            replace: true,
            search: (previous) => ({ ...previous, current: previous.current - 1 })
          })
        }
      }
    })
  }

  const changeStatus = async (organization: OrganizationRow) => {
    try {
      await setDisabled.mutateAsync({ id: organization.id, isDisabled: !organization.isDisable })
      void message.success(organization.isDisable ? '经营主体已恢复' : '经营主体已停用')
      await queryClient.invalidateQueries(trpc.organization.list.queryFilter())
    } catch (error) {
      void message.error(getErrorMessage(error, '主体状态更新失败'))
    }
  }
  const table = usePageTableNavigation<OrganizationQuery, OrganizationSearchFormValues>({
    defaultSearch: DEFAULT_ORGANIZATION_SEARCH,
    getFormInitialValues: (currentSearch) => ({
      keyword: currentSearch.keyword ?? (currentSearch.id === undefined ? undefined : String(currentSearch.id)),
      operationMode: currentSearch.operationMode,
      status: currentSearch.status
    }),
    getSearchFromForm: (values) => ({
      id: undefined,
      keyword: values.keyword?.trim() || undefined,
      operationMode: values.operationMode,
      status: values.status
    }),
    navigate: navigateSearch,
    search: query,
    showTotal: (total) => `共 ${total} 个主体`
  })

  const columns: ProColumns<OrganizationRow>[] = [
    {
      dataIndex: 'keyword',
      fieldProps: { allowClear: true, placeholder: '主体名称、统一社会信用代码或 ID' },
      hideInTable: true,
      title: '关键词 / ID'
    },
    {
      dataIndex: 'id',
      copyable: true,
      ellipsis: true,
      renderText: (value?: number) => (value === undefined ? '—' : String(value)),
      search: false,
      title: 'ID',
      width: '7rem'
    },
    {
      dataIndex: 'name',
      ellipsis: true,
      render: (_, organization) => (
        <Space className="w-full min-w-0" orientation="vertical" size={0}>
          <Typography.Text className="block max-w-full truncate" strong>
            {organization.name}
          </Typography.Text>
          <Typography.Text className="block max-w-full truncate" copyable type="secondary">
            {organization.unifiedSocialCreditCode || '未补充统一社会信用代码'}
          </Typography.Text>
        </Space>
      ),
      search: false,
      title: '经营主体',
      width: '16rem'
    },
    {
      dataIndex: 'siteCount',
      title: '总地址数',
      tooltip: '归属该主体的场地总数，包含已停用场地',
      search: false,
      align: 'right',
      width: '8rem'
    },
    {
      dataIndex: 'accountCount',
      title: '资金账户数',
      tooltip: '归属该主体的资金账户总数，包含已停用账户',
      search: false,
      align: 'right',
      width: '8rem'
    },
    {
      dataIndex: 'operationMode',
      title: '经营类型',
      valueEnum: {
        agent: { text: '代理' },
        selfOperated: { text: '自营' }
      },
      render: (_, organization) => {
        const mode = OPERATION_MODE[organization.operationMode]
        return <Tag color={mode.color}>{mode.text}</Tag>
      },
      width: '7rem'
    },
    {
      dataIndex: 'legalRepresentative',
      ellipsis: true,
      renderText: (value?: string | null) => value || '—',
      search: false,
      title: '法定代表人',
      width: '9rem'
    },
    {
      dataIndex: 'registeredAddress',
      ellipsis: true,
      search: false,
      title: '注册地址',
      width: '20rem'
    },
    {
      dataIndex: 'status',
      hideInTable: true,
      title: '状态',
      valueEnum: {
        enabled: { text: '正常' },
        disabled: { text: '已停用' }
      }
    },
    {
      dataIndex: 'isDisable',
      render: (_, organization) => (
        <Tag color={organization.isDisable ? 'default' : 'success'}>{organization.isDisable ? '已停用' : '正常'}</Tag>
      ),
      search: false,
      title: '状态',
      width: '6.5rem'
    },
    {
      dataIndex: 'creator',
      render: (_, organization) => (
        <UserAvatarName avatar={organization.creator.avatar} name={organization.creator.name} size={28} />
      ),
      search: false,
      title: '创建人',
      width: '10rem'
    },
    {
      dataIndex: 'createdAt',
      search: false,
      title: '创建时间',
      valueType: 'dateTime',
      width: '12rem'
    },
    {
      dataIndex: 'updatedAt',
      search: false,
      title: '最近更新',
      valueType: 'dateTime',
      width: '12rem'
    },
    {
      align: 'center',
      fixed: 'right',
      render: (_, organization) =>
        canManage ? (
          <TableRowActions
            resource="organization"
            actions={[
              {
                key: 'edit',
                label: '编辑',
                render: (
                  <Link
                    params={{ organizationId: String(organization.id) }}
                    search={query}
                    to="/master-data/legal-entities/$organizationId/edit"
                  >
                    编辑
                  </Link>
                )
              },
              {
                key: 'status',
                label: organization.isDisable ? '恢复' : '停用',
                danger: !organization.isDisable,
                disabled: setDisabled.isPending || deleteOrganization.isPending,
                onClick: () => {
                  modal.confirm({
                    title: organization.isDisable ? '确认恢复主体？' : '确认停用主体？',
                    content: organization.isDisable ? '恢复后可继续用于业务。' : '停用后历史数据仍会保留。',
                    okButtonProps: { danger: !organization.isDisable },
                    onOk: () => changeStatus(organization)
                  })
                }
              },
              {
                key: 'delete',
                label: '删除',
                danger: true,
                disabled: setDisabled.isPending || deleteOrganization.isPending,
                onClick: () => confirmDelete(organization)
              }
            ]}
          />
        ) : (
          '—'
        ),
      search: false,
      title: '操作',
      valueType: 'option',
      width: '10rem'
    }
  ]

  return (
    <PageContainer
      breadcrumbRender={false}
      content="维护当前经营中的公司主体、营业执照与工商登记信息。"
      title="主体设置"
    >
      {organizations.isError ? (
        <Alert
          action={
            <Button onClick={() => void organizations.refetch()} size="small">
              重试
            </Button>
          }
          className="mb-4"
          showIcon
          title={getErrorMessage(organizations.error, '主体列表加载失败')}
          type="error"
        />
      ) : null}
      <ProTable<OrganizationRow, OrganizationSearchFormValues>
        columns={columns}
        columnsState={{ persistenceKey: 'master-data-legal-entities:table', persistenceType: 'localStorage' }}
        dataSource={organizations.data?.data ?? []}
        loading={{ delay: 150, spinning: organizations.isFetching }}
        onReset={table.onReset}
        onSubmit={table.onSubmit}
        options={{ reload: () => void organizations.refetch() }}
        pagination={{ ...table.pagination, total: organizations.data?.total ?? 0 }}
        formRef={table.formRef}
        rowKey="id"
        scroll={{ x: '118rem' }}
        form={{ initialValues: table.formInitialValues }}
        search={{ defaultCollapsed: false, labelWidth: 'auto' }}
        size="small"
        toolBarRender={() =>
          canManage
            ? [
                <Link key="new" search={query} to="/master-data/legal-entities/new">
                  <Authorized permissions={['organization.create']}>
                    <Button icon={<Plus aria-hidden size={16} />} type="primary">
                      新建主体
                    </Button>
                  </Authorized>
                </Link>
              ]
            : []
        }
      />
    </PageContainer>
  )
}
